import { createSelector } from 'reselect';
import { getIsApmERPJobFlow } from './getIsApmERPJobFlow';
import { getProgramEvents } from './getProgramEvents';
import { getIsSetupModeActive } from './getIsSetupModeActive';
import { getOpenLaborTicketRefs } from './getOpenLaborTicketRefs';

/**
 * Returns the active order quantity for an APM ERP job flow machine, derived
 * from the most recent visible program event with an order quantity.
 *
 * Visibility rules match useProgramEventDisplayState visibleEvents:
 * - Exclude events with closedDate
 * - Exclude confirmed events (confirmedAt set) whose laborTicketRef is not in
 *   the open labor ticket set (same logic as hook's visibleEvents filter)
 *
 * Returns null for non-APM flows, during setup mode, and when no active event exists.
 */
export const getApmCurrentOrderQuantity = createSelector(
  [
    getIsApmERPJobFlow,
    getProgramEvents,
    getIsSetupModeActive,
    getOpenLaborTicketRefs,
  ],
  (isApmERPJobFlow, programEvents, isSetupModeActive, openLaborTicketRefs) => {
    if (!isApmERPJobFlow || isSetupModeActive) {
      return null;
    }

    const openLtRefSet = new Set(openLaborTicketRefs);

    const activeEvents = programEvents
      .filter((event) => {
        if (event.closedDate) return false;
        if (event.orderQuantity == null) return false;
        if (event.confirmedAt && event.laborTicketRef) {
          return openLtRefSet.has(event.laborTicketRef);
        }
        return true;
      })
      .slice()
      .sort((a, b) => {
        const aTime = a.eventTime ? new Date(a.eventTime).getTime() : 0;
        const bTime = b.eventTime ? new Date(b.eventTime).getTime() : 0;
        return bTime - aTime;
      });

    const activeEvent = activeEvents[0];
    if (!activeEvent) {
      return null;
    }

    const { orderQuantity, workOrderOperationRef } = activeEvent;

    return {
      workOrderOperationRef:
        workOrderOperationRef != null ? String(workOrderOperationRef) : null,
      completedQuantity: orderQuantity?.completedQuantity ?? 0,
      finishQuantity: orderQuantity?.finishQuantity ?? null,
      uncoveredParts: orderQuantity?.uncoveredParts ?? null,
      submittedBadParts: orderQuantity?.submittedBadParts ?? 0,
    };
  }
);
